import * as ISchema from "#webapp/src/__schema__";
import { CalendarTimeStats, UserWorkingHours } from "@clockwise/schema";
import { time } from "@clockwise/web-commons/src/styles/color.styles";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { compact } from "lodash/fp";
import React, { useCallback, useEffect, useMemo, useState } from "react";

// constants
import { daysOfWeek, dayToIndex } from "#webapp/src/constants/date.constant";

const labels = {
  FOCUS_TIME: "Focus Time",
  MEETINGS: "meetings",
  OTHER: "other events",
  FRAGMENTED_TIME: "fragmented time",
};

export const ProfileTimeOverWeek = ({
  calendarTimeStats,
  userWorkingHours,
}: {
  calendarTimeStats: CalendarTimeStats;
  userWorkingHours: UserWorkingHours;
}) => {
  const [graphStartDay, setGraphStartDay] = useState(ISchema.Day.Monday);
  const [graphEndDay, setGraphEndDay] = useState(ISchema.Day.Friday);

  const getRoundedHours = useCallback((minutes: number) => {
    if (minutes > 600) {
      // if over 10, round to nearest integer
      return Math.round(minutes / 60);
    } else {
      // otherwise round to the nearest .5
      return Math.round(minutes / 30) / 2;
    }
  }, []);

  const scoresByDay = useMemo(() => {
    return compact(calendarTimeStats?.scoresByDay) || [];
  }, [calendarTimeStats?.scoresByDay]);

  const daySettings = useMemo(() => {
    return compact(userWorkingHours?.workingHours?.daySettings) || [];
  }, [userWorkingHours?.workingHours?.daySettings]);

  useEffect(() => {
    // Only expand graph if user's working hours have Sunday or Saturday.
    for (const daySetting of daySettings) {
      if (daySetting.day === ISchema.Day.Sunday) {
        setGraphStartDay(ISchema.Day.Sunday);
      } else if (daySetting.day === ISchema.Day.Saturday) {
        setGraphEndDay(ISchema.Day.Saturday);
      }
    }
  }, [daySettings]);

  const relevantDays = useMemo(() => {
    return daysOfWeek.slice(dayToIndex[graphStartDay], dayToIndex[graphEndDay] + 1);
  }, [graphEndDay, graphStartDay]);

  const xAxisLabels = useMemo(() => {
    return relevantDays.map((day) => {
      return day.substring(0, 3);
    });
  }, [relevantDays]);

  const focusMinutesByDay = useMemo(() => {
    return relevantDays.map((day) => {
      const dayScore = scoresByDay.find((score) => score.secondaryKey === day);
      return (dayScore && getRoundedHours(dayScore?.focusMinutes || 0)) || 0;
    });
  }, [relevantDays, scoresByDay, getRoundedHours]);

  const meetingsByDay = useMemo(() => {
    return relevantDays.map((day) => {
      const dayScore = scoresByDay.find((score) => score.secondaryKey === day);
      return (dayScore && getRoundedHours(dayScore?.busyMinutes || 0)) || 0;
    });
  }, [relevantDays, scoresByDay, getRoundedHours]);

  const config = useMemo(() => {
    return {
      series: [
        {
          color: time.focus,
          name: labels.FOCUS_TIME,
          data: focusMinutesByDay,
        },
        {
          color: time.busy,
          name: labels.MEETINGS,
          data: meetingsByDay,
        },
      ],
      chart: {
        height: 200,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        headerFormat: "<b>{point.x}</b><br/>",
        pointFormat: "{series.name}: {point.y} hr",
      },
      title: {
        text: "",
        marginTop: 0,
      },
      xAxis: {
        categories: xAxisLabels,
        tickColor: "transparent",
        gridLineColor: "transparent",
      },
      yAxis: {
        title: {
          text: null,
        },
        labels: {
          enabled: false,
        },
        min: 0,
        max: Math.max(...focusMinutesByDay, ...meetingsByDay) || 12,
        gridLineColor: "transparent",
      },
    };
  }, [focusMinutesByDay, xAxisLabels, meetingsByDay]);

  return <HighchartsReact highcharts={Highcharts} options={config} />;
};
